import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { useState } from 'react';
import { MoreHorizontal, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { P, usePermissions } from '@/features/permissions';
import { type AudioSegmentOut, useDeleteAudioSegment } from '../api/encounters-api';
import {
  AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export function DeleteRecordingButton({ segment, encounterId }: { encounterId: string; segment: AudioSegmentOut }) {
  const { can } = usePermissions();
  const remove = useDeleteAudioSegment(encounterId);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  if (!can(P.UPDATE_ENCOUNTER)) return null;
  async function confirm() {
    if (remove.isPending) return;
    setError('');
    try {
      await remove.mutateAsync(segment.id);
      setOpen(false);
      toast.success('Recording deleted');
    } catch {
      setError('Could not delete this recording. Please try again.');
    }
  }
  return <AlertDialog open={open} onOpenChange={value => { if (!remove.isPending) setOpen(value); }}>
    <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="outline" size="icon" className="min-h-11 min-w-11" aria-label={`Recording actions for recording ${segment.order}`} disabled={remove.isPending}>
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuGroup>
              <DropdownMenuItem variant="destructive" onSelect={() => { setError(''); setOpen(true); }}>
                <Trash2 />Delete recording
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Delete recording {segment.order}?</AlertDialogTitle>
        <AlertDialogDescription>
          This permanently deletes this recording and its segment transcription. Other recordings and the encounter remain.
          Existing encounter transcripts and generated reports are not rewritten; regenerate the report if needed after deletion.
        </AlertDialogDescription>
      </AlertDialogHeader>
      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
      <AlertDialogFooter>
        <AlertDialogCancel disabled={remove.isPending}>Cancel</AlertDialogCancel>
        <Button type="button" variant="destructive" disabled={remove.isPending} onClick={() => void confirm()}>{remove.isPending ? 'Deleting…' : 'Delete this recording'}</Button>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>;
}
